import './underwriting.quote.sort.options.scss';

import {
  Box,
  Card,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';
import {
  ConvergeButton,
  DialogLayout,
  PrimaryTitle,
} from '../../../presentation';
import {
  GET_QUOTES,
  QuoteDetails,
  useUpsertQuoteMutation,
} from '@converge/graphql';
import { useCallback, useState } from 'react';
import {
  useQuoteListActionsContext,
  useQuoteListDispatchContext,
  useQuoteListStateContext,
} from './quote-list.context';

import IconButton from '@mui/material/IconButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Stack } from '@mui/system';
import { formatDollarAmount } from '@converge/utils';
import { underwritingQuoteToUpsertVariables } from './utils';
import { useConvergeSnackBar } from '../../../contexts';
import { useTranslation } from 'react-i18next';

const StyledTableCell = styled(TableCell)(() => ({
  border: 'none',
}));
interface IUnderwritingSortQuoteOptionsProps {
  submissionUuid: string;
}
export const UnderwritingSortQuoteOptions = ({
  submissionUuid,
}: IUnderwritingSortQuoteOptionsProps) => {
  const dispatch = useQuoteListDispatchContext();
  const actions = useQuoteListActionsContext();

  const { showQuoteOrderModel, quotes: originalQuotes } =
    useQuoteListStateContext();
  const { notify } = useConvergeSnackBar();
  const { upsertQuote, loading } = useUpsertQuoteMutation();

  const [quotes, setQuotes] = useState<QuoteDetails[]>(originalQuotes);
  const [draggedQuote, setDraggedQuote] = useState<QuoteDetails | null>(null);

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>, quote: QuoteDetails) => {
      event.dataTransfer.setData('quoteUUID', quote.uuid);
      setDraggedQuote(quote);
    },
    [setDraggedQuote]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>, quote: QuoteDetails) => {
      event.preventDefault();

      if (draggedQuote && draggedQuote.uuid !== quote.uuid) {
        setQuotes((prevState) => {
          const updatedQuotes = prevState.filter(
            (q) => q.uuid !== draggedQuote.uuid
          );
          const insertIndex = prevState.findIndex((q) => q.uuid === quote.uuid);
          updatedQuotes.splice(insertIndex, 0, draggedQuote);

          return updatedQuotes.map((q, index) => {
            return {
              ...q,
              order: index + 1,
            };
          });
        });
      }
    },
    [draggedQuote, setQuotes]
  );

  const handleDrop = useCallback(() => {
    setDraggedQuote(null);
  }, [setDraggedQuote]);

  const shiftQuote = useCallback(
    (uuid: string, direction: string) => {
      setQuotes((prevState) => {
        const updatedArray = [...prevState];
        const index = updatedArray.findIndex((quote) => quote.uuid === uuid);
        if (
          index === -1 ||
          (index === 0 && direction === 'up') ||
          (index === updatedArray.length - 1 && direction === 'down')
        ) {
          return prevState;
        }

        if (direction === 'up') {
          [updatedArray[index - 1], updatedArray[index]] = [
            updatedArray[index],
            updatedArray[index - 1],
          ];
        } else if (direction === 'down') {
          [updatedArray[index], updatedArray[index + 1]] = [
            updatedArray[index + 1],
            updatedArray[index],
          ];
        }

        return updatedArray.map((quote, index) => {
          return {
            ...quote,
            order: index + 1,
          };
        });
      });
    },
    [setQuotes]
  );

  const saveQuote = useCallback(async () => {
    const filteredQuotes = quotes.filter((quote) => {
      const prevQuote = originalQuotes.find(
        (q) => q.uuid === quote.uuid
      )?.order;
      if (prevQuote) {
        return quote.order !== prevQuote;
      }
      return true;
    });
    try {
      notify('Saving quotes order...', {
        variant: 'info',
      });
      await Promise.all(
        filteredQuotes.map((quote) =>
          upsertQuote({
            variables: underwritingQuoteToUpsertVariables({
              quote,
              submissionUuid: submissionUuid,
            }),
            awaitRefetchQueries: true,
            update: (cache, { data: updatedData }) => {
              if (updatedData && !updatedData.upsertQuote) {
                throw new Error(
                  `Quote was not created, and the API did not return an error.`
                );
              }

              if (updatedData?.upsertQuote?.uuid) {
                const data = cache.readQuery({
                  query: GET_QUOTES,
                });
                cache.writeQuery({
                  query: GET_QUOTES,
                  data: {
                    ...data,
                    quotes: {
                      ...data?.quotes,
                      edges: {
                        ...data?.quotes.edges,
                        ...data?.quotes.edges.map((q) => {
                          if (q?.node?.uuid === quote.uuid) {
                            return {
                              ...q,
                              node: {
                                ...q.node,
                                order: quote.order,
                              },
                            };
                          }
                          return q;
                        }),
                      },
                    },
                  },
                });
              }
            },
          })
        )
      );

      notify(`Quote saved.`, {
        variant: 'success',
      });
    } catch (error) {
      notify(
        `Failed to save quotes: ${
          error instanceof Error ? error.message : 'unknown error'
        }`,
        {
          variant: 'error',
        }
      );
    }
    dispatch(actions.toggleSortQuoteModel(false));
  }, [
    quotes,
    originalQuotes,
    actions,
    dispatch,
    notify,
    submissionUuid,
    upsertQuote,
  ]);

  return (
    <DialogLayout
      noHeaderPaddingY
      header={
        <Stack className="sort-quote-modal-header">
          <Box>
            <Typography fontWeight={'bold'} marginRight={1}>
              Sort Quote Options
            </Typography>
          </Box>
        </Stack>
      }
      open={showQuoteOrderModel}
      maxWidth={false}
      onCloseIconClick={() => dispatch(actions.toggleSortQuoteModel(false))}
      onClose={() => dispatch(actions.toggleSortQuoteModel(false))}
      showCloseButton
      grayFooter
      footer={<RaterContentsFooter onClick={saveQuote} isLoading={loading} />}
      noPadding
      scrollContents
      TransitionComponent={Slide}
    >
      <Stack minWidth="700px" width="100%">
        <Box className="sort-quote-modal-body">
          {quotes.map((quote, index) => (
            <RaterContentsQuoteCard
              key={quote.uuid}
              quote={quote}
              handleDrop={handleDrop}
              handleDragOver={handleDragOver}
              handleDragStart={handleDragStart}
              isLast={index === quotes.length - 1}
              isFirst={index === 0}
              shiftQuote={shiftQuote}
            />
          ))}
        </Box>
      </Stack>
    </DialogLayout>
  );
};
export const RaterContentsFooter = ({
  onClick,
  isLoading,
}: {
  onClick: () => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      flexGrow={1}
      flexDirection={'row'}
      alignItems={'center'}
      justifyContent={'flex-end'}
    >
      <ConvergeButton
        label={t('underwriting.quote.header.order-button', 'Save')}
        loading={isLoading}
        onClick={() => onClick()}
      />
    </Stack>
  );
};

export const RaterContentsQuoteCard = ({
  quote,
  handleDrop,
  handleDragOver,
  handleDragStart,
  isLast,
  isFirst,
  shiftQuote,
}: {
  quote: QuoteDetails;
  handleDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    quote: QuoteDetails
  ) => void;
  handleDragOver: (
    event: React.DragEvent<HTMLDivElement>,
    quote: QuoteDetails
  ) => void;
  handleDrop: () => void;
  isLast: boolean;
  isFirst: boolean;
  shiftQuote: (uuid: string, direction: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Card
      draggable
      onDragStart={(event) => handleDragStart(event, quote)}
      onDragOver={(event) => handleDragOver(event, quote)}
      onDrop={handleDrop}
      onDragEnd={handleDrop}
      sx={{
        opacity: quote.isObsolete ? 0.5 : 1,
      }}
      className="quote-card"
    >
      <Table aria-label="quote-card">
        <TableHead>
          <TableRow>
            <StyledTableCell
              align="center"
              colSpan={2}
              className="rater-quote-card-styled-table-cell"
            >
              <Typography component="p" fontSize={14}>
                {quote.referenceId} - {quote.status}
              </Typography>
            </StyledTableCell>
          </TableRow>
          <TableRow>
            <StyledTableCell
              className="rater-quote-card-styled-table-cell"
              align="center"
              colSpan={2}
            >
              <Typography variant="h4">{quote.name}</Typography>
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ backgroundColor: '#f3f3f3' }}>
          <TableRow className="header-amount">
            <StyledTableCell
              colSpan={2}
              className="rater-quote-card-styled-table-cell"
            >
              <div className="quotes-flex-wrapper">
                <div>
                  <span>
                    {t(
                      'underwriting.quote.grid.summary.aggregateLimit',
                      'Aggregate limit'
                    )}
                  </span>
                  <PrimaryTitle>{formatDollarAmount(quote.limit)}</PrimaryTitle>
                </div>
                <div className="right-span">
                  <span>
                    {t('underwriting.quote.grid.summary.premium', 'Premium')}
                  </span>
                  <PrimaryTitle>
                    {formatDollarAmount(quote.quotedPremium)}
                  </PrimaryTitle>
                </div>
              </div>
            </StyledTableCell>
          </TableRow>
        </TableBody>
      </Table>
      <div className="sort-action-button">
        <IconButton
          aria-label="up"
          size="small"
          className="up"
          disabled={isFirst}
          onClick={() => shiftQuote(quote.uuid, 'up')}
        >
          <PlayArrowIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="down"
          size="small"
          className="down"
          disabled={isLast}
          onClick={() => shiftQuote(quote.uuid, 'down')}
        >
          <PlayArrowIcon fontSize="small" />
        </IconButton>
      </div>
    </Card>
  );
};
